import type { WorkspaceResponseDto } from '@monksflow/platform-api-sdk';
import { Avatar, AvatarFallback, Badge, Tabs, TabsList, TabsTrigger } from '@monksflow/monks-ui';

import { ORG_WIDE_SCOPE } from './constants';
import { useTranslation } from 'react-i18next';

type ScopeTab = string;

interface WorkspaceScopeTabsProps {
  activeScope: ScopeTab;
  onScopeChange: (scope: ScopeTab) => void;
  currentWorkspace: WorkspaceResponseDto | null;
  otherWorkspaces: WorkspaceResponseDto[];
}

function WorkspaceTabLabel({
  workspace,
  isCurrent,
}: {
  workspace: WorkspaceResponseDto;
  isCurrent?: boolean;
}) {
  const { t } = useTranslation('admin');

  return (
    <span className="flex flex-row items-center gap-2">
      {workspace.logoUrl ? (
        <img
          src={workspace.logoUrl}
          alt={workspace.name}
          className="size-6 shrink-0 rounded object-cover"
        />
      ) : (
        <Avatar className="size-6 shrink-0 rounded">
          <AvatarFallback className="rounded bg-muted text-xs font-semibold">
            {workspace.name.charAt(0).toUpperCase()}
          </AvatarFallback>
        </Avatar>
      )}
      <span>{workspace.name}</span>
      {isCurrent && (
        <Badge variant="secondary" className="h-4 px-1.5 py-0 text-[10px] leading-none">
          {t('admin.workspaceScopeTabs.current')}
        </Badge>
      )}
    </span>
  );
}

export const WorkspaceScopeTabs = ({
  activeScope,
  onScopeChange,
  currentWorkspace,
  otherWorkspaces,
}: WorkspaceScopeTabsProps) => {
  const { t } = useTranslation('admin');
  return (
    <Tabs value={activeScope} onValueChange={onScopeChange}>
      <TabsList className="h-auto max-w-full justify-start overflow-x-auto">
        {currentWorkspace && (
          <TabsTrigger key={currentWorkspace.id} value={currentWorkspace.id}>
            <WorkspaceTabLabel workspace={currentWorkspace} isCurrent />
          </TabsTrigger>
        )}
        <TabsTrigger value={ORG_WIDE_SCOPE}>
          {t('admin.workspaceScopeTabs.organization')}
        </TabsTrigger>
        {otherWorkspaces.map((workspace) => (
          <TabsTrigger key={workspace.id} value={workspace.id}>
            <WorkspaceTabLabel workspace={workspace} />
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
};
